import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { DEFAULT_LAYOUT_SETTINGS } from '../types/row-node';
import { createDefaultLevelConfig } from '../utils/column-config-utils';
import { buildCellFormula, buildSumFormula, columnIndexToLetter, getNumberFormat, hexToArgb, } from './formula-builder';
import { compileReport } from './report-compiler';
import { renderToExcel } from './excel-renderer';
// ============================================================
// Main export
// ============================================================
export async function buildWorkbook(definition, dataContext, nodes, columnConfig, layoutSettings, instructionText) {
    const workbook = new ExcelJS.Workbook();
    // Step 1: Instructions sheet (if provided, placed first)
    if (instructionText) {
        buildInstructionsSheet(workbook, instructionText, definition.name);
    }
    // Step 2: Parameters sheet
    buildParametersSheet(workbook, definition, dataContext);
    // Step 3-4: Report sheet
    const effectiveColumnConfig = columnConfig ?? createDefaultLevelConfig(definition.columns);
    const effectiveLayout = layoutSettings ?? DEFAULT_LAYOUT_SETTINGS;
    if (nodes && nodes.length > 0) {
        buildReportSheetFromTree(workbook, definition, nodes, effectiveColumnConfig, effectiveLayout, dataContext);
    }
    else {
        buildReportSheet(workbook, definition, dataContext);
    }
    const buffer = await workbook.xlsx.writeBuffer();
    return { workbook, buffer };
}
export async function generateExcel(definition, dataContext, nodes, columnConfig, layoutSettings, instructionText) {
    const { buffer } = await buildWorkbook(definition, dataContext, nodes, columnConfig, layoutSettings, instructionText);
    const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, `${definition.name}.xlsx`);
}
// ============================================================
// Instructions sheet
// ============================================================
function buildInstructionsSheet(workbook, instructionText, reportName) {
    const sheet = workbook.addWorksheet('Instructions');
    sheet.getColumn(1).width = 80;
    // Title
    const titleCell = sheet.getCell('A1');
    titleCell.value = reportName;
    titleCell.font = { bold: true, size: 16 };
    // Subtitle
    const subtitleCell = sheet.getCell('A2');
    subtitleCell.value = 'Template Instructions';
    subtitleCell.font = { bold: true, size: 12, color: { argb: 'FF666666' } };
    // Blank row
    let row = 4;
    // Write instruction text line by line
    const lines = instructionText.split('\n');
    for (const line of lines) {
        const cell = sheet.getCell(`A${row}`);
        cell.value = line;
        // Style section headers (ALL CAPS lines)
        if (/^[A-Z][A-Z\s&:]+$/.test(line.trim()) && line.trim().length > 3) {
            cell.font = { bold: true, size: 11 };
        }
        else if (line.startsWith('- ') || line.startsWith('  -')) {
            cell.font = { size: 10, color: { argb: 'FF333333' } };
        }
        else if (line.startsWith('IMPORTANT') || line.startsWith('NOTE')) {
            cell.font = { bold: true, size: 10, color: { argb: 'FFCC6600' } };
        }
        else {
            cell.font = { size: 10 };
        }
        cell.alignment = { wrapText: true, vertical: 'top' };
        row++;
    }
    // Tab color for visual distinction
    sheet.properties.tabColor = { argb: 'FF4472C4' };
}
// ============================================================
// Parameters sheet
// ============================================================
function buildParametersSheet(workbook, definition, dataContext) {
    const sheet = workbook.addWorksheet('Parameters');
    sheet.getColumn(1).width = 15;
    sheet.getColumn(2).width = 30;
    const companyCell = sheet.getCell('A1');
    companyCell.value = 'Company';
    companyCell.font = { bold: true };
    const companyValueCell = sheet.getCell('B1');
    companyValueCell.value = dataContext.companies[0]?.name ?? '';
    companyValueCell.dataValidation = {
        type: 'list',
        allowBlank: false,
        formulae: [`"${dataContext.companies.map((c) => c.name).join(',')}"`],
    };
    // Company ID lookup: C1 resolves company name → integer ID via hidden lookup table in F:G
    const companies = dataContext.companies;
    if (companies.length > 0) {
        // Write hidden lookup table: F = company name, G = company ID (integer)
        for (let i = 0; i < companies.length; i++) {
            sheet.getCell(`F${i + 1}`).value = companies[i].name;
            const idNum = Number(companies[i].id);
            sheet.getCell(`G${i + 1}`).value = Number.isFinite(idNum) ? idNum : companies[i].id;
        }
        // Hide the lookup columns
        sheet.getColumn(6).hidden = true;
        sheet.getColumn(7).hidden = true;
        // C1 = INDEX/MATCH to resolve company name to ID
        const lastRow = companies.length;
        const companyIdCell = sheet.getCell('C1');
        companyIdCell.value = {
            formula: `IFERROR(INDEX($G$1:$G$${lastRow},MATCH(B1,$F$1:$F$${lastRow},0)),"")`,
        };
        sheet.getColumn(3).width = 5;
        companyIdCell.font = { color: { argb: 'FF999999' }, size: 9 };
    }
    const periodCell = sheet.getCell('A2');
    periodCell.value = 'Period';
    periodCell.font = { bold: true };
    const periodValueCell = sheet.getCell('B2');
    periodValueCell.value = dataContext.periods[0]?.name ?? '';
    periodValueCell.dataValidation = {
        type: 'list',
        allowBlank: false,
        formulae: [`"${dataContext.periods.map((p) => p.name).join(',')}"`],
    };
    // Connection dropdown at B3 - used by CLM functions
    const connectionCell = sheet.getCell('A3');
    connectionCell.value = 'Connection';
    connectionCell.font = { bold: true };
    const connectionValueCell = sheet.getCell('B3');
    const connectionNames = dataContext.erpConnections?.map((c) => c.name) ?? [];
    connectionValueCell.value = connectionNames[0] ?? '';
    if (connectionNames.length > 0) {
        connectionValueCell.dataValidation = {
            type: 'list',
            allowBlank: false,
            formulae: [`"${connectionNames.join(',')}"`],
        };
    }
    let paramRow = 4;
    for (const param of definition.parameters) {
        if (param.type === 'company' || param.type === 'period')
            continue;
        sheet.getCell(`A${paramRow}`).value = param.name;
        sheet.getCell(`A${paramRow}`).font = { bold: true };
        if (param.type === 'boolean') {
            sheet.getCell(`B${paramRow}`).value = param.defaultValue === 'true' ? 'Yes' : 'No';
            sheet.getCell(`B${paramRow}`).dataValidation = {
                type: 'list',
                allowBlank: false,
                formulae: ['"Yes,No"'],
            };
        }
        else if (param.defaultValue) {
            sheet.getCell(`B${paramRow}`).value = param.defaultValue;
        }
        paramRow++;
    }
    // ── Date & Fiscal Information block ──
    if (dataContext.dateFiscalInfo) {
        const info = dataContext.dateFiscalInfo;
        paramRow++; // spacer row
        const sectionFont = { bold: true, size: 11, underline: true };
        sheet.getCell(`A${paramRow}`).value = 'DATE & FISCAL INFORMATION';
        sheet.getCell(`A${paramRow}`).font = sectionFont;
        paramRow++;
        const dateRows = [
            ['Current Year', info.currentYear],
            ['Period Start', info.currentPeriodStartDate],
            ['Period End', info.currentPeriodEndDate],
            ['Fiscal Year Start', info.fiscalYearStart],
            ['Fiscal Year End', info.fiscalYearEnd],
        ];
        for (const [label, value] of dateRows) {
            sheet.getCell(`A${paramRow}`).value = label;
            sheet.getCell(`A${paramRow}`).font = { bold: true };
            const cell = sheet.getCell(`B${paramRow}`);
            cell.value = value;
            if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
                cell.numFmt = 'yyyy-mm-dd';
            }
            paramRow++;
        }
    }
    // ── ERP Connections block ──
    if (dataContext.erpConnections && dataContext.erpConnections.length > 0) {
        paramRow++; // spacer row
        const sectionFont = { bold: true, size: 11, underline: true };
        sheet.getCell(`A${paramRow}`).value = 'ERP CONNECTIONS';
        sheet.getCell(`A${paramRow}`).font = sectionFont;
        paramRow++;
        for (const conn of dataContext.erpConnections) {
            sheet.getCell(`A${paramRow}`).value = conn.name;
            sheet.getCell(`B${paramRow}`).value = conn.erpType.toUpperCase();
            paramRow++;
        }
    }
    // ── Company Data Sources block ──
    const companiesWithConnections = dataContext.companies.filter((c) => c.erpConnectionName);
    if (companiesWithConnections.length > 0) {
        paramRow++; // spacer row
        const sectionFont = { bold: true, size: 11, underline: true };
        sheet.getCell(`A${paramRow}`).value = 'COMPANY DATA SOURCES';
        sheet.getCell(`A${paramRow}`).font = sectionFont;
        paramRow++;
        // Column headers
        sheet.getCell(`A${paramRow}`).value = 'Company';
        sheet.getCell(`A${paramRow}`).font = { bold: true };
        sheet.getCell(`B${paramRow}`).value = 'ERP Connection';
        sheet.getCell(`B${paramRow}`).font = { bold: true };
        paramRow++;
        for (const company of companiesWithConnections) {
            sheet.getCell(`A${paramRow}`).value = company.name;
            sheet.getCell(`B${paramRow}`).value = company.erpConnectionName ?? '';
            paramRow++;
        }
    }
}
// ============================================================
// Tree-based report sheet (uses compiler + renderer pipeline)
// ============================================================
function buildReportSheetFromTree(workbook, definition, nodes, columnConfig, layoutSettings, dataContext) {
    const sheet = workbook.addWorksheet(definition.name);
    const { theme } = definition;
    // Step 1: Compile to renderer-agnostic RenderRow[]
    const renderRows = compileReport(nodes, columnConfig, theme, dataContext);
    // Step 2: Render to Excel
    renderToExcel(sheet, renderRows, layoutSettings, theme);
}
// ============================================================
// Legacy flat report sheet (backward compat)
// ============================================================
function buildReportSheet(workbook, definition, dataContext) {
    const sheet = workbook.addWorksheet(definition.name);
    const { columns, rows, theme } = definition;
    const columnLetterMap = new Map();
    columns.forEach((col, idx) => {
        const letter = columnIndexToLetter(idx);
        if (!columnLetterMap.has(col.type)) {
            columnLetterMap.set(col.type, letter);
        }
    });
    columns.forEach((col, idx) => {
        sheet.getColumn(idx + 1).width = col.type === 'label' ? (col.width ?? 30) : (col.width ?? 15);
    });
    writeColumnHeaders(sheet, columns, theme);
    const rowMap = new Map();
    let excelRow = 2;
    for (const row of rows) {
        excelRow = renderRow(sheet, row, excelRow, columns, columnLetterMap, theme, rowMap, dataContext);
    }
}
// ============================================================
// Column headers
// ============================================================
function writeColumnHeaders(sheet, columns, theme, row = 1) {
    columns.forEach((col, idx) => {
        const cell = sheet.getCell(row, idx + 1);
        cell.value = col.header;
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
        cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: hexToArgb(theme.colors.headerBackground) },
        };
        cell.alignment = { horizontal: col.type === 'label' || col.type === 'account_code' ? 'left' : 'center' };
    });
}
// ============================================================
// Row rendering (dispatch by type — legacy flat path only)
// ============================================================
function renderRow(sheet, row, excelRow, columns, columnLetterMap, theme, rowMap, dataContext, labelPrefix = '', rowIdToExcelRow, nodeRowId) {
    let resultRow;
    switch (row.type) {
        case 'header':
            resultRow = renderHeaderRow(sheet, row, excelRow, columns, theme, rowMap);
            break;
        case 'section':
            resultRow = renderSectionRow(sheet, row, excelRow, columns, columnLetterMap, theme, rowMap, dataContext, labelPrefix);
            break;
        case 'account':
            resultRow = renderAccountRow(sheet, row, excelRow, columns, columnLetterMap, theme, rowMap, dataContext, labelPrefix);
            break;
        case 'subtotal':
            resultRow = renderSubtotalRow(sheet, row, excelRow, columns, columnLetterMap, theme, rowMap, labelPrefix);
            break;
        case 'calculated':
            resultRow = renderCalculatedRow(sheet, row, excelRow, columns, theme, rowMap, labelPrefix);
            break;
        case 'spacer':
            rowMap.set(row.id, [excelRow]);
            resultRow = excelRow + 1;
            break;
        case 'line':
            resultRow = renderLineRow(sheet, row.borderStyle, excelRow, columns, rowMap, row.id);
            break;
        case 'grand_total':
            resultRow = renderGrandTotalRow(sheet, row, excelRow, columns, theme, rowMap, labelPrefix);
            break;
        case 'title_block':
            // Title blocks are handled separately in buildReportSheetFromTree
            // If they reach here (legacy path), just skip
            resultRow = excelRow;
            break;
        case 'column_headers':
            // Column headers are handled separately in buildReportSheetFromTree
            resultRow = excelRow;
            break;
    }
    // Record rowId -> Excel row mapping
    if (rowIdToExcelRow && nodeRowId) {
        rowIdToExcelRow.set(nodeRowId, excelRow);
    }
    return resultRow;
}
// ============================================================
// Header row
// ============================================================
function renderHeaderRow(sheet, row, excelRow, columns, theme, rowMap) {
    const colCount = columns.length;
    if (colCount > 1) {
        sheet.mergeCells(excelRow, 1, excelRow, colCount);
    }
    const cell = sheet.getCell(excelRow, 1);
    cell.value = row.text;
    const fontSize = row.level === 1 ? 14 : row.level === 2 ? 12 : 11;
    cell.font = { bold: true, size: fontSize, color: { argb: 'FFFFFFFF' } };
    cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: hexToArgb(theme.colors.headerBackground) },
    };
    rowMap.set(row.id, [excelRow]);
    return excelRow + 1;
}
// ============================================================
// Section row (expands to N account rows + optional subtotal)
// ============================================================
function renderSectionRow(sheet, row, excelRow, columns, columnLetterMap, theme, rowMap, dataContext, labelPrefix = '') {
    const accounts = filterAccounts(row.filter, dataContext.accounts);
    const sectionDataRows = [];
    for (const account of accounts) {
        writeAccountDataRow(sheet, account, excelRow, columns, columnLetterMap, theme, labelPrefix);
        sectionDataRows.push(excelRow);
        excelRow++;
    }
    rowMap.set(row.id, sectionDataRows);
    if (row.showSubtotal && sectionDataRows.length > 0) {
        const label = row.subtotalLabel ?? 'Subtotal';
        writeSubtotalDataRow(sheet, labelPrefix + label, excelRow, columns, columnLetterMap, sectionDataRows, theme, 'bold');
        rowMap.set(row.id + '__subtotal', [excelRow]);
        excelRow++;
    }
    return excelRow;
}
// ============================================================
// Account row (single account)
// ============================================================
function renderAccountRow(sheet, row, excelRow, columns, columnLetterMap, theme, rowMap, dataContext, labelPrefix = '') {
    const account = dataContext.accounts.find((a) => a.code === row.accountCode);
    const displayName = row.label ?? account?.name ?? row.accountCode;
    const fakeAccount = {
        code: row.accountCode,
        name: displayName,
        type: account?.type ?? 'asset',
        parent: account?.parent ?? null,
    };
    writeAccountDataRow(sheet, fakeAccount, excelRow, columns, columnLetterMap, theme, labelPrefix);
    rowMap.set(row.id, [excelRow]);
    return excelRow + 1;
}
// ============================================================
// Subtotal row
// ============================================================
function renderSubtotalRow(sheet, row, excelRow, columns, columnLetterMap, theme, rowMap, labelPrefix = '') {
    const dataRows = collectRowNumbers(row.sumOf, rowMap);
    writeSubtotalDataRow(sheet, labelPrefix + row.label, excelRow, columns, columnLetterMap, dataRows, theme, row.style);
    rowMap.set(row.id, [excelRow]);
    return excelRow + 1;
}
// ============================================================
// Calculated row
// ============================================================
function renderCalculatedRow(sheet, row, excelRow, columns, theme, rowMap, labelPrefix = '') {
    columns.forEach((col, idx) => {
        const cell = sheet.getCell(excelRow, idx + 1);
        if (col.type === 'label') {
            cell.value = labelPrefix + row.label;
        }
        else if (col.type === 'account_code') {
            cell.value = '';
        }
        else if (row.formula) {
            cell.value = { formula: row.formula };
        }
        if (col.format) {
            cell.numFmt = getNumberFormat(col.format, theme.negativeFormat);
        }
    });
    applyRowStyle(sheet, excelRow, columns.length, row.style);
    rowMap.set(row.id, [excelRow]);
    return excelRow + 1;
}
// ============================================================
// Grand total row
// ============================================================
function renderGrandTotalRow(sheet, row, excelRow, columns, theme, rowMap, labelPrefix = '') {
    const dataRows = collectRowNumbers(row.sumOf, rowMap);
    columns.forEach((col, idx) => {
        const cell = sheet.getCell(excelRow, idx + 1);
        const colLetter = columnIndexToLetter(idx);
        if (col.type === 'label') {
            cell.value = labelPrefix + row.label;
        }
        else if (col.type === 'account_code') {
            cell.value = '';
        }
        else if (dataRows.length > 0) {
            cell.value = { formula: buildSumFormula(colLetter, dataRows) };
        }
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
        cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: hexToArgb(theme.colors.headerBackground) },
        };
        cell.border = {
            bottom: { style: 'double' },
        };
        if (col.format) {
            cell.numFmt = getNumberFormat(col.format, theme.negativeFormat);
        }
    });
    rowMap.set(row.id, [excelRow]);
    return excelRow + 1;
}
function renderLineRow(sheet, borderStyle, excelRow, columns, rowMap, rowId) {
    const excelBorderStyle = borderStyle === 'double' ? 'double'
        : borderStyle === 'thick' ? 'thick'
            : borderStyle === 'medium' ? 'medium'
                : 'thin';
    const border = { style: excelBorderStyle, color: { argb: '000000' } };
    // Apply bottom border to all cells in the row
    columns.forEach((_, idx) => {
        const cell = sheet.getCell(excelRow, idx + 1);
        cell.border = { bottom: border };
    });
    // Set a small row height for line rows
    const excelRowObj = sheet.getRow(excelRow);
    excelRowObj.height = 6;
    rowMap.set(rowId, [excelRow]);
    return excelRow + 1;
}
// ============================================================
// Shared helpers
// ============================================================
function writeAccountDataRow(sheet, account, excelRow, columns, columnLetterMap, theme, labelPrefix = '') {
    columns.forEach((col, idx) => {
        const cell = sheet.getCell(excelRow, idx + 1);
        if (col.type === 'label') {
            cell.value = labelPrefix + account.name;
        }
        else if (col.type === 'account_code') {
            cell.value = account.code;
        }
        else {
            // Resolve base/compare column letters for variance columns
            const baseColLetter = col.baseColumnId
                ? columnIndexToLetter(columns.findIndex((c) => c.id === col.baseColumnId))
                : undefined;
            const compareColLetter = col.compareColumnId
                ? columnIndexToLetter(columns.findIndex((c) => c.id === col.compareColumnId))
                : undefined;
            const formula = buildCellFormula({
                columnType: col.type,
                accountCode: account.code,
                excelRow,
                columnLetterMap,
                userFormula: col.formula,
                periodOffset: col.periodOffset,
                budgetName: col.budgetName,
                baseColumnLetter: baseColLetter,
                compareColumnLetter: compareColLetter,
            });
            if (formula) {
                cell.value = { formula };
            }
        }
        if (col.format) {
            cell.numFmt = getNumberFormat(col.format, theme.negativeFormat);
        }
    });
}
function writeSubtotalDataRow(sheet, label, excelRow, columns, _columnLetterMap, dataRows, theme, style) {
    columns.forEach((col, idx) => {
        const cell = sheet.getCell(excelRow, idx + 1);
        const colLetter = columnIndexToLetter(idx);
        if (col.type === 'label') {
            cell.value = label;
        }
        else if (col.type === 'account_code') {
            cell.value = '';
        }
        else if (dataRows.length > 0) {
            cell.value = { formula: buildSumFormula(colLetter, dataRows) };
        }
        cell.font = { bold: true };
        cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: hexToArgb(theme.colors.subtotalBackground) },
        };
        if (col.format) {
            cell.numFmt = getNumberFormat(col.format, theme.negativeFormat);
        }
    });
    applyRowStyle(sheet, excelRow, columns.length, style);
}
function filterAccounts(filter, accounts) {
    let expandedCodes = filter.codes;
    if (filter.codes?.length) {
        const parentCodes = new Set(accounts.filter((a) => a.parent === null).map((a) => a.code));
        const expanded = new Set();
        for (const code of filter.codes) {
            if (parentCodes.has(code)) {
                for (const acct of accounts) {
                    if (acct.parent === code)
                        expanded.add(acct.code);
                }
            }
            else {
                expanded.add(code);
            }
        }
        expandedCodes = expanded.size > 0 ? [...expanded] : undefined;
    }
    return accounts.filter((account) => {
        if (account.parent === null)
            return false;
        if (filter.types?.length && !filter.types.includes(account.type))
            return false;
        if (filter.parentCode && account.parent !== filter.parentCode)
            return false;
        if (expandedCodes?.length && !expandedCodes.includes(account.code))
            return false;
        if (filter.excludeCodes?.length && filter.excludeCodes.includes(account.code))
            return false;
        return true;
    });
}
function collectRowNumbers(sumOfIds, rowMap) {
    const result = [];
    for (const id of sumOfIds) {
        const rows = rowMap.get(id);
        if (rows) {
            result.push(...rows);
        }
    }
    return result;
}
function applyRowStyle(sheet, excelRow, colCount, style) {
    if (!style || style === 'normal')
        return;
    for (let c = 1; c <= colCount; c++) {
        const cell = sheet.getCell(excelRow, c);
        if (style === 'bold') {
            cell.font = { ...cell.font, bold: true };
        }
        else if (style === 'underline') {
            cell.border = { ...cell.border, bottom: { style: 'thin' } };
        }
        else if (style === 'double_underline') {
            cell.border = { ...cell.border, bottom: { style: 'double' } };
        }
    }
}
//# sourceMappingURL=excel-generator.js.map